import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext, BACKEND_URL } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Sparkles, 
  Save,
  Globe,
  Calendar,
  Loader2,
  Upload,
  Image,
  X,
  Facebook,
  Linkedin,
  Instagram,
  Share2
} from 'lucide-react';

const AdminBlogManagement = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    meta_description: '',
    keywords: '',
    featured_image: '',
    status: 'draft',
    category: '',
    tags: ''
  });

  const [aiRequest, setAiRequest] = useState({
    topic: '',
    category: 'Luxury Travel',
    tone: 'professional',
    length: 'medium'
  });

  const categories = [
    'Luxury Travel',
    'Miami Events',
    'Corporate Transportation',
    'Airport Transfers',
    'Wedding Transportation',
    'Travel Tips'
  ];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or GIF image');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/blog-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Serve static files through /api so production ingress routes to backend
      const imageUrl = `${BACKEND_URL}/api${response.data.image_url}`;
      setFormData(prev => ({ ...prev, featured_image: imageUrl }));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, featured_image: '' }));
  };

  const handleAIGenerate = async () => {
    if (!aiRequest.topic) {
      toast.error('Please enter a topic');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/blog/generate`, aiRequest);
      const generated = response.data;
      
      setFormData({
        title: generated.title || aiRequest.topic,
        content: generated.content || '',
        excerpt: generated.excerpt || '',
        meta_description: generated.meta_description || '',
        keywords: (generated.keywords || []).join(', '),
        featured_image: '',
        status: 'draft',
        category: generated.category || aiRequest.category,
        tags: ''
      });
      
      setShowAIDialog(false);
      setShowEditor(true);
      toast.success('Blog post generated! Review and edit before publishing.');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate blog post');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()).filter(k => k) : [],
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
      };

      if (editingPost) {
        await axios.put(`${API}/admin/blog/posts/${editingPost.id}`, payload);
        toast.success('Blog post updated!');
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload);
        toast.success('Blog post created!');
      }
      
      setShowEditor(false);
      setEditingPost(null);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error('Failed to save blog post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      content: post.content || '',
      excerpt: post.excerpt || '',
      meta_description: post.meta_description || '',
      keywords: (post.keywords || []).join(', '),
      featured_image: post.featured_image || '',
      status: post.status || 'draft',
      category: post.category || '',
      tags: (post.tags || []).join(', ')
    });
    setShowEditor(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      await axios.delete(`${API}/admin/blog/posts/${postId}`);
      toast.success('Blog post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      meta_description: '',
      keywords: '',
      featured_image: '',
      status: 'draft',
      category: '',
      tags: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            Loading blog posts...
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showEditor) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-6 h-6" />
                {editingPost ? 'Edit Blog Post' : 'Create Blog Post'}
              </CardTitle>
              <Button variant="outline" onClick={() => { setShowEditor(false); setEditingPost(null); resetForm(); }}>
                Cancel
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter blog post title"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="excerpt">Excerpt (Short preview)</Label>
                  <Textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    placeholder="Brief description shown in listings"
                    rows={2}
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="content">Content * (HTML supported)</Label>
                  <Textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Write your blog post content here..."
                    rows={15}
                    required
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="meta_description">SEO Meta Description (155 chars max)</Label>
                  <Textarea
                    id="meta_description"
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleInputChange}
                    placeholder="Brief description for search engines"
                    rows={2}
                    maxLength={160}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.meta_description.length}/160 characters
                  </p>
                </div>

                <div>
                  <Label htmlFor="keywords">SEO Keywords (comma-separated)</Label>
                  <Input
                    id="keywords"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleInputChange}
                    placeholder="luxury transportation, miami limo, etc."
                  />
                </div>

                <div>
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    placeholder="miami, luxury, events"
                  />
                </div>

                <div className="col-span-2">
                  <Label>Featured Image</Label>
                  <div className="mt-2">
                    {formData.featured_image ? (
                      <div className="relative inline-block">
                        <img 
                          src={formData.featured_image} 
                          alt="Featured" 
                          className="max-h-48 rounded-lg border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={handleRemoveImage}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div 
                        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {uploading ? (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                            <p className="text-gray-500">Uploading...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Image className="w-8 h-8 text-gray-400" />
                            <p className="text-gray-500">Click to upload featured image</p>
                            <p className="text-xs text-gray-400">JPEG, PNG, WebP, GIF up to 10MB</p>
                          </div>
                        )}
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp,image/gif"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                  <div className="mt-2">
                    <Label htmlFor="featured_image_url" className="text-xs text-gray-500">Or enter image URL directly:</Label>
                    <Input
                      id="featured_image_url"
                      name="featured_image"
                      value={formData.featured_image}
                      onChange={handleInputChange}
                      placeholder="https://..."
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <Button type="submit" className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {editingPost ? 'Update Post' : 'Create Post'}
                </Button>
                {formData.status === 'draft' && (
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, status: 'published' }));
                    }}
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Publish Now
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <FileText className="w-8 h-8" />
          Blog Management
        </h1>
        <div className="flex gap-2">
          <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI Generate
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-gold-500" />
                  Generate Blog Post with AI
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Topic / Title Idea *</Label>
                  <Input
                    value={aiRequest.topic}
                    onChange={(e) => setAiRequest(prev => ({ ...prev, topic: e.target.value }))}
                    placeholder="e.g., Top 10 Miami Events This Summer"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={aiRequest.category} onValueChange={(v) => setAiRequest(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tone</Label>
                    <Select value={aiRequest.tone} onValueChange={(v) => setAiRequest(prev => ({ ...prev, tone: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="informative">Informative</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Length</Label>
                    <Select value={aiRequest.length} onValueChange={(v) => setAiRequest(prev => ({ ...prev, length: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Short (400-600 words)</SelectItem>
                        <SelectItem value="medium">Medium (800-1200 words)</SelectItem>
                        <SelectItem value="long">Long (1500-2000 words)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button 
                  onClick={handleAIGenerate} 
                  className="w-full"
                  disabled={generating || !aiRequest.topic}
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Blog Post
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Button onClick={() => { resetForm(); setShowEditor(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            New Post
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Blog Posts ({posts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No blog posts yet.</p>
              <p className="text-gray-400 text-sm">Create your first post to start building your blog!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {posts.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="font-medium">{post.title}</div>
                        <div className="text-sm text-gray-500">/blog/{post.slug}</div>
                      </td>
                      <td className="px-4 py-4">
                        <Badge variant="outline">{post.category || 'Uncategorized'}</Badge>
                      </td>
                      <td className="px-4 py-4">
                        <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                          {post.status === 'published' ? (
                            <><Globe className="w-3 h-3 mr-1" /> Published</>
                          ) : (
                            'Draft'
                          )}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4 text-gray-400" />
                          {post.views || 0}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(post.published_at || post.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          {post.status === 'published' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                                title="View Post"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {/* Social Share Dropdown */}
                              <div className="relative group">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-600"
                                  title="Share to Social Media"
                                >
                                  <Share2 className="w-4 h-4" />
                                </Button>
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                                  <div className="p-2 space-y-1">
                                    <a
                                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${BACKEND_URL}/blog/${post.slug}`)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded-md"
                                    >
                                      <Facebook className="w-4 h-4 text-[#1877F2]" />
                                      Share on Facebook
                                    </a>
                                    <a
                                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`${BACKEND_URL}/blog/${post.slug}`)}&title=${encodeURIComponent(post.title)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded-md"
                                    >
                                      <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                                      Share on LinkedIn
                                    </a>
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(`${BACKEND_URL}/blog/${post.slug}`);
                                        toast.success('Link copied for Instagram!');
                                      }}
                                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded-md w-full text-left"
                                    >
                                      <Instagram className="w-4 h-4 text-[#E4405F]" />
                                      Copy for Instagram
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(post)}
                            title="Edit Post"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(post.id)}
                            title="Delete Post"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBlogManagement;
